/**
 * A handy history utility that manages localstorage create/edit/delete
 */
namespace appn.dataHistory {

    /**
     * The max number of items to keep in storage
     * Older items are removed to maintain this number
     */
    const _historyMaxLimit: number = 20;

    /**
     * The prefix used for the key part of the local storage entry
     */
    const _keyPrefix: string = 'appn-data-history-';

    /**
     * Creates or Edits the data history array
     * @param id Local storage history Key
     * @param dataHistoryItemObj New Item to create or edit
     */
    export function createOrEditHistory(id: string, dataHistoryItemObj: IDataHistoryItem) {
        // Validate params
        if (!id || $.isEmptyObject(dataHistoryItemObj) || dataHistoryItemObj && (!dataHistoryItemObj.hasOwnProperty('key') || !dataHistoryItemObj.hasOwnProperty('value')))
            return;

        // Check for current LS history
        const appnDataHistoryParsed: any = appn.localStorage.getItem(_keyPrefix + id);

        // Create or Edit History
        if ($.isEmptyObject(appnDataHistoryParsed)) {
            // Create new history object
            appn.localStorage.setItem(_keyPrefix + id, [dataHistoryItemObj]);
            return;
        }

        // Pull and parse
        if (appnDataHistoryParsed.length > _historyMaxLimit)
            // Prune the end by 1 to make space for new entry
            appnDataHistoryParsed.pop();

        let alreadyExists = false;
        let indexToBumpToTop = -1;
        for (let i = 0; i < appnDataHistoryParsed.length; i++)
            if (appnDataHistoryParsed[i].key.trim().toLowerCase() === dataHistoryItemObj.key.trim().toLowerCase()) {
                // Replace if already exists
                appnDataHistoryParsed[i].value = dataHistoryItemObj.value;
                appnDataHistoryParsed[i].timeStamp = new Date().getTime();
                alreadyExists = true;
                indexToBumpToTop = i;
                break;
            }

        if (!alreadyExists)
            // Only add if the email matches an email in the value
            appnDataHistoryParsed.unshift(dataHistoryItemObj);
        else {
            // Bump to the top of the list
            const itemToBump = appnDataHistoryParsed[indexToBumpToTop];
            appnDataHistoryParsed.splice(indexToBumpToTop, 1);
            appnDataHistoryParsed.unshift(itemToBump);
        }

        // Save the changes to local storage
        appn.localStorage.setItem(_keyPrefix + id, appnDataHistoryParsed);
    }

    /**
     * Creates or Edits the data history array
     * @param id Local storage history Key
     * @param key Item to delete
     */
    export function deleteItemObj(id: string, key: string) {
        // Validate params
        if (!id || !key)
            return;

        // Check for current LS history
        const appnDataHistoryParsed: any = appn.localStorage.getItem(_keyPrefix + id);

        // Create or Edit History
        if ($.isEmptyObject(appnDataHistoryParsed)) {
            // Create new history object
            appn.localStorage.setItem(_keyPrefix + id, []);
            return;
        }

        // Pull and parse
        let indexToRemove = -1;
        for (let i = 0; i < appnDataHistoryParsed.length; i++)
            if (appnDataHistoryParsed[i].key.trim().toLowerCase() === key.trim().toLowerCase()) {
                indexToRemove = i;
                break;
            }

        if (indexToRemove !== -1)
            // Delete
            appnDataHistoryParsed.splice(indexToRemove, 1);

        // Save the changes to local storage
        appn.localStorage.setItem(_keyPrefix + id, appnDataHistoryParsed);
    }

    /**
     * Gets a data history array from LS
     * @param id Local storage history Key
     */
    export function getDataHistory(id: string): Array<IDataHistoryItem> {
        // Validate params
        if (!id)
            return [];

        const appnDataHistoryParsed: any = appn.localStorage.getItem(_keyPrefix + id);
        if (!appnDataHistoryParsed)
            return [];

        if (!appnDataHistoryParsed || appnDataHistoryParsed.length === 0)
            return [];

        return appnDataHistoryParsed;
    }

    export interface IDataHistoryItem {
        key: string;
        value: any;
        timeStamp: number;
    }
}
